import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d9n1jpu8mbnljin59ss0';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const testimonials = [
    {
      name: '张先生',
      role: '影视爱好者',
      content: '用了桃子视频之后，再也不用到处找资源了。91视频和97视频的资源更新特别快，画质也很清晰，真的是追剧神器！',
      rating: 5,
    },
    {
      name: '李女士',
      role: '全职妈妈',
      content: '小蓝视频的加载速度真的快，在家里用WiFi看高清完全不卡。51视频的电视剧资源特别全，全家人都喜欢。',
      rating: 5,
    },
    {
      name: '王同学',
      role: '大学生',
      content: '92视频和98视频的电影资源太丰富了，周末和室友一起看电影成了固定节目。妖精视频的动漫资源也很赞！',
      rating: 5,
    },
  ];

  const stats = [
    { label: '视频资源', value: '10万+' },
    { label: '数字系列平台', value: '7个' },
    { label: '月活跃用户', value: '5000万+' },
    { label: '用户好评率', value: '98%' },
  ];

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    try {
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          urlEncoded.append(key, String(value));
        }
      });

      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const isSpam = responseText.includes('spam') || responseText.includes('form data is spam');
      const codeOk = parsed?.code === 'OK';

      if (response.ok && codeOk && !isSpam) {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '桃子视频是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，桃子视频所有内容均免费提供，无需注册或付费即可观看。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系桃子视频客服？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本页的联系表单或发送邮件至contact@taozivideo.com联系我们。',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="桃子视频品牌介绍、用户口碑与联系方式。桃子视频数字系列视频平台，91视频、97视频、51视频等热门资源免费高清在线观看。了解我们的品牌故事和用户评价。"
        keywords="桃子视频,关于我们,品牌介绍,联系方式,用户口碑"
        canonicalUrl="https://taozivideo.com/about"
        schema={faqSchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="w-full bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
          <div className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-7xl mx-auto text-center">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                关于桃子视频
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-3xl mx-auto">
                桃子视频是一个聚合型数字系列视频平台，致力于为广大用户提供免费、高清、流畅的在线视频观看体验。
              </p>
            </div>
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-16">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                  品牌故事
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    桃子视频诞生于数字时代的浪潮之中。我们观察到，尽管市面上视频平台众多，但用户仍然面临资源分散、画质参差不齐、加载缓慢等问题。于是，我们决定打造一个聚合型的数字系列视频平台。
                  </p>
                  <p>
                    从最初的91视频到如今涵盖91视频、97视频、51视频、92视频、98视频、小蓝视频、妖精视频七大数字系列平台，桃子视频始终坚持"免费、高清、不卡顿"的核心理念。
                  </p>
                  <p>
                    我们汇聚了海量影视资源——电影、电视剧、动漫、综艺、纪录片，应有尽有。无论你是追剧达人还是电影发烧友，在桃子视频都能找到心仪的内容。小蓝视频以极速加载著称，妖精视频则专注精品推荐，每个平台都有其独特的魅力。
                  </p>
                  <p>
                    展望未来，桃子视频将继续优化技术体验，丰富内容资源，让每一位用户都能享受到最优质的在线观影服务。
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                  联系我们
                </h2>
                <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
                  <form
                    id="contact-form"
                    data-readdy-form=""
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        您的姓名 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        电子邮箱 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="请输入主题"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容 <span className="text-accent-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
                        placeholder="请输入您的留言（500字以内）"
                      />
                    </div>

                    <div className="honeypot-field">
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'loading' ? '提交中...' : '发送留言'}
                    </button>

                    {formStatus === 'success' && (
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-primary-50 text-primary-700 text-sm">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-checkbox-circle-fill text-sm"></i>
                        </div>
                        {formMessage}
                      </div>
                    )}

                    {formStatus === 'error' && (
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-accent-50 text-accent-700 text-sm">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-error-warning-fill text-sm"></i>
                        </div>
                        {formMessage}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500">听听用户怎么说</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 rounded-xl border border-background-200/70 p-6"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: t.rating }, (_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center text-primary-500">
                        <i className="ri-star-fill text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div>
                    <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                    <div className="text-xs text-foreground-400">{t.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-7xl mx-auto text-center">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
              加入我们的观影社区
            </h2>
            <p className="text-sm text-foreground-500 mb-8 max-w-xl mx-auto">
              随时随地，畅享高清影视。桃子视频，让好内容触手可及。
            </p>
            <Link
              to="/"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex px-8 py-3 bg-primary-500 text-background-50 rounded-full font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              立即体验
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}